import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import {
  patientService,
  PatientSecurity,
} from "../../services/patient.service";

interface Props {
  patientId: number;
}

const PatientSecurityTab: React.FC<Props> = ({ patientId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [security, setSecurity] = useState<PatientSecurity | null>(null);

  useEffect(() => {
    loadSecurity();
  }, [patientId]);

  const loadSecurity = async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientSecurity(patientId);
      setSecurity(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load security settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!security) return;

    try {
      setSaving(true);
      setSuccess(false);
      setError("");
      await patientService.updatePatientSecurity(patientId, security);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to update security settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof PatientSecurity, value: any) => {
    if (!security) return;
    setSecurity({ ...security, [field]: value });
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!security) {
    return <Alert severity='error'>{error || "No data found"}</Alert>;
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Security Settings
      </Typography>

      {success && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Security settings updated successfully
        </Alert>
      )}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Username'
            fullWidth
            required
            value={security.username || ""}
            onChange={(e) => handleChange("username", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='System Type ID'
            fullWidth
            type='number'
            value={security.systemTypeID || 0}
            onChange={(e) =>
              handleChange("systemTypeID", parseInt(e.target.value))
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label='Security Code (4-digit PIN)'
            fullWidth
            inputProps={{ maxLength: 4 }}
            value={security.securityCode || ""}
            onChange={(e) => handleChange("securityCode", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label='Security Question ID'
            fullWidth
            type='number'
            value={security.securityQuestionID || ""}
            onChange={(e) =>
              handleChange("securityQuestionID", parseInt(e.target.value))
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label='Security Answer'
            fullWidth
            value={security.securityAnswer || ""}
            onChange={(e) => handleChange("securityAnswer", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientSecurityTab;
